import { TransformedValues, useForm, yupResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { UseMutationOptions, useQueryClient, useMutation } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { Asserts } from "yup";
import { captureException } from "@/services/log";
import { TLicenciadaForm, updateLicenciada } from "../../firestore";
import licenciadaSchema from "../../schema/licenciada-schema";
import { QUERY_KEY } from "../licenciadas-table/LicenciadasTable";

type LicenciadaInputs = Asserts<typeof licenciadaSchema>;
type TUpdateLicenciadaResponse = Awaited<ReturnType<typeof updateLicenciada>>;

/**
 * Licenciada Form hook
 * @param licenciada - Licenciada data
 * @param root0 - Options
 * @param root0.onSuccess - On success callback
 * @param root0.onError - On error callback
 * @returns - Licenciada Form
 */
function useLicenciadaForm(
  licenciada: TLicenciadaForm & Partial<Pick<TLicenciada, "id" | "refPath">>,
  {
    onSuccess,
    onError
  }: Pick<UseMutationOptions<TUpdateLicenciadaResponse, FirestoreError, LicenciadaInputs>, "onSuccess" | "onError">
) {
  const defaultLicenciadaValues = licenciadaSchema.getDefault() as TLicenciadaForm;

  const queryClient = useQueryClient();
  const form = useForm<LicenciadaInputs>({
    validate: yupResolver(licenciadaSchema),
    transformValues: (values) => licenciadaSchema.cast(values),
    initialValues: {
      ...defaultLicenciadaValues,
      ...licenciada
    }
  });

  const { mutate, isPending, reset } = useMutation<TUpdateLicenciadaResponse, FirestoreError, LicenciadaInputs>({
    mutationFn: async (values) => {
      if (!licenciada.id) {
        throw new Error("ID da licenciada não encontrado");
      }
      return updateLicenciada(licenciada.id, values);
    },
    async onSuccess(data, variables, context) {
      showNotification({
        message: `${variables.razaoSocial} atualizada com sucesso!`,
        color: "green",
        autoClose: 2500
      });
      reset();
      form.reset();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      if (onSuccess) {
        return onSuccess(data, variables, context);
      }
      return null;
    },
    onError(error, variables, context) {
      captureException(error, true);
      if (onError) {
        onError(error, variables, context);
      }
    }
  });

  type TFormTransformed = TransformedValues<typeof form>;

  /**
   * Handle form submit
   * @param values - Form values
   */
  function handleFormSubmit(values: TFormTransformed) {
    mutate(values);
  }

  return { form, isPending, onSubmit: form.onSubmit(handleFormSubmit) };
}

export default useLicenciadaForm;
